import { useEffect, useMemo, useState } from "react";
import { DatePicker, Select, Form, Input } from "antd";
import { getUsers } from "../../api/user";
import { getEquipments } from "../../api/equipment";
import { getProjects } from "../../api/project";
import dayjs from "dayjs";

export default function TaskForm({
  onFormInstanceReady,
  projectUsers,
  projectEquipments,
  initialValues,
  projects,
}) {
  const [form] = Form.useForm();
  const [searchUser, setSearchUser] = useState(projectUsers || []);
  const [searchEquipment, setSearchEquipmentOptions] = useState(
    projectEquipments || []
  );
  const [searchProject, setSearchProject] = useState(projects || [])

  const userSearchOptions = useMemo(() => {
    return searchUser.map((user) => {
      return {
        value: user.id,
        label: user.firstName + " " + (user.lastName || ""),
      };
    });
  }, [searchUser]);

  const searchEquipmentOptions = useMemo(() => {
    return searchEquipment.map((equipment) => {
      return {
        value: equipment.id,
        label: equipment.name,
      };
    });
  }, [searchEquipment]);

  const searchProjectOptions = useMemo(() => {
    return searchProject.map((project) => {
      return {
        value: project.id,
        label: project.name,
      };
    })
  }, [searchProject]);

  const handleStartDateChange = () => {
    const endDate = form.getFieldValue("dueDate");
    if (!endDate) {
      form.setFieldsValue({
        dueDate: dayjs(form.getFieldValue("startDate")).add(8, "hours"),
      });
    }
  }

  useEffect(() => {
    onFormInstanceReady(form);
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        assignee: initialValues.assignee.id,
        equipment: initialValues.equipment?.id,
        startDate: dayjs(initialValues.startDate),
        dueDate: dayjs(initialValues.dueDate),
      });
      setSearchEquipmentOptions([{
        ...initialValues.equipment
      }]);
      setSearchUser([{ ...initialValues.assignee }])
    }
  }, [form, onFormInstanceReady, initialValues]);

  return (
    <Form form={form} layout="vertical" >
      <div className="grid grid-cols-2 gap-x-4">
        <Form.Item name={"name"} label="Task Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>

        <Form.Item
          name={"assignee"}
          label="Assignee"
          rules={[{ required: true }]}
        >
          <Select
            placeholder={"Search to Select"}
            showSearch
            options={userSearchOptions}
            onSearch={async (str) => {
              const { results: users } = await getUsers({ q: str });
              setSearchUser(users);
            }}
            rules={[{ required: true }]}
            defaultActiveFirstOption={false}
            filterOption={false}
            mode="tag"
            allowClear
          />
        </Form.Item>

        <Form.Item name={"equipment"} label="Equipment" rules={[{ required: true }]}>
          <Select
            placeholder={"Search to Select"}
            showSearch
            options={searchEquipmentOptions}
            onSearch={async (str) => {
              const { results: users } = await getEquipments({ q: str });
              setSearchEquipmentOptions(users);
            }}
            rules={[{ required: true }]}
            defaultActiveFirstOption={false}
            filterOption={false}
            mode="tag"
            allowClear
          />
        </Form.Item>

        <Form.Item name={"project"} label="Project" rules={[{ required: true }]}>
          <Select
            placeholder={"Search to Select"}
            showSearch
            options={searchProjectOptions}
            onSearch={async (str) => {
              const { results: projects } = await getProjects({ q: str });
              setSearchProject(projects);
            }}
            rules={[{ required: true }]}
            defaultActiveFirstOption={false}
            filterOption={false}
            mode="tag"
            allowClear
          />
        </Form.Item>

        <Form.Item name={"startDate"} label="Start Date">
          <DatePicker className="w-full" showTime onChange={handleStartDateChange} />
        </Form.Item>

        <Form.Item name={"dueDate"} label="Due Date"
          rules={[
            {
              validator: async (rule, value) => {
                if (value <= form.getFieldValue("startDate")) {
                  return Promise.reject("Must be greater than Start Date");
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <DatePicker className="w-full" showTime />
        </Form.Item>

        <Form.Item
          name={"description"}
          label="Task Description"
          className="col-span-2"
          rules={[{ required: true }]}
        >
          <Input.TextArea />
        </Form.Item>
      </div>
    </Form>
  );
}
